import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    effect,
    input,
    Injector,
    output,
    QueryList,
    ViewChildren,
    ViewEncapsulation,
    computed,
    inject,
    OnInit,
    untracked,
} from '@angular/core';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import {
    IChecklistTimeInterval,
    IChecklistTimeIntervalData,
    IChecklistTimeIntervalGroup,
} from '../../../../checklist-v2';
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import { FiChecklistScheduleType } from '../../fi-checklist-schedule-type';
import { FiChecklistTimeIntervalType } from '@fi-shared/checklist/schedule/fi-checklist-time-interval-type';
import { FiChecklist2ScheduleFactoryService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-factory.service';
import { FiChecklist2ScheduleFormatService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-format.service';
import { FiChecklist2ScheduleIntervalService } from '@fi-shared/checklist/schedule/services/fi-checklist2-schedule-interval.service';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { FiChecklist2TimeIntervalSettingsComponent } from '@fi-shared/checklist/schedule/schedule-edit/schedule-time/time-intervals/time-interval-settings/time-interval-settings.component';
import { FiToggleSwitchSliderColorClass } from '@fi-shared/fi-toggle-switch/fi-toggle-switch.component';
import { FiSnackBarService } from '@fi-shared/fi-snackbar/snack-bar.service';
import { AppTextService } from '@fi-shared/app-text';
import { FiTimePickerHelperService } from '@fi-shared/fi-time-picker-input/fi-time-picker-helper.service';

export interface IChecklistTimeIntervalIdentifier {
    scheduleTimeIntervalKey: string;
    intervalIndex: number;
}

@Component({
    selector: 'fi-checklist2-time-intervals',
    templateUrl: './time-intervals.component.html',
    styleUrls: ['./time-intervals.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-checklist2-time-intervals' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: FiChecklist2TimeIntervalsComponent,
        },
        {
            provide: NG_VALIDATORS,
            multi: true,
            useExisting: FiChecklist2TimeIntervalsComponent,
        },
    ],
    standalone: false,
})
export class FiChecklist2TimeIntervalsComponent
    extends ControlValueAccessorValidatorBase<IChecklistTimeIntervalGroup>
    implements OnInit
{
    public hasAnswers = input<boolean>();
    public intervalType = input<FiChecklistTimeIntervalType>();
    public scheduleType = input<FiChecklistScheduleType>();
    public invalidTimeIntervalIdentifier = input<IChecklistTimeIntervalIdentifier>();
    public skipIntervalErrorHighlighting = input<boolean>();
    public timeIntervalChanged = output<IChecklistTimeIntervalIdentifier | undefined>();

    private fiChecklist2ScheduleIntervalService = inject(FiChecklist2ScheduleIntervalService);
    private fiChecklist2ScheduleFormatService = inject(FiChecklist2ScheduleFormatService);
    private fiChecklist2ScheduleFactoryService = inject(FiChecklist2ScheduleFactoryService);
    private fiSnackBarService = inject(FiSnackBarService);
    private appTextService = inject(AppTextService);
    private fiTimePickerHelperService = inject(FiTimePickerHelperService);

    @ViewChildren(FiChecklist2TimeIntervalSettingsComponent)
    timeIntervalSettingsComponents: QueryList<FiChecklist2TimeIntervalSettingsComponent>;
    @ViewChildren('scheduleTimeIntervalWrapper')
    scheduleTimeIntervalWrappers: QueryList<ElementRef<HTMLElement>>;

    protected ObjectKeys = Object.keys;

    protected isRecurringSchedule = computed<boolean>(() =>
        this.fiChecklist2ScheduleFactoryService.isRecurringChecklistSchedule(this.scheduleType()),
    );
    protected isDailyInterval = computed<boolean>(
        () => this.intervalType() === FiChecklistTimeIntervalType.RecurringDaily,
    );
    protected isMonthlyInterval = computed<boolean>(() =>
        this.fiChecklist2ScheduleIntervalService.isMonthlyInterval(this.intervalType()),
    );
    protected hasExtraIntervals = computed<boolean>(() =>
        this.fiChecklist2ScheduleIntervalService.hasExtraIntervals(this.intervalType()),
    );
    private isNotDailyOrWeeklyInterval = computed<boolean>(
        () => !this.isDailyInterval() && this.intervalType() !== FiChecklistTimeIntervalType.RecurringWeekly,
    );
    private isOneTimeSchedule = computed<boolean>(() =>
        this.fiChecklist2ScheduleIntervalService.isOneTimeSchedule(this.intervalType()),
    );
    protected isStartTimeDisabled = computed<boolean>(() => this.hasAnswers() && this.isOneTimeSchedule());

    constructor(injector: Injector) {
        super(injector);

        effect(() => {
            const invalidTimeIntervalIdentifier = this.invalidTimeIntervalIdentifier();
            if (invalidTimeIntervalIdentifier) {
                setTimeout(() => this.scrollToTimeIntervalGroup(invalidTimeIntervalIdentifier));
            }
        });

        effect(() => {
            const scheduleType = this.scheduleType();
            untracked(() => {
                this.setPreviousIntervalGroups();
            });
        });
    }

    public ngOnInit(): void {
        this.setPreviousIntervalGroups();
    }

    protected isEnabledInterval(intervalGroupKey: string): boolean {
        return this.ngModel[intervalGroupKey][0].IsEnabled;
    }

    protected toggleIsEnabledInterval(intervalGroupKey: string, isEnabled: boolean): void {
        this.ngModel[intervalGroupKey].map((interval: IChecklistTimeInterval) => {
            interval.IsEnabled = isEnabled;
            return interval;
        });
        this.emitTimeIntervalChanged();
    }

    protected isRemoveButtonShown(intervalGroupKey: string): boolean {
        return this.hasExtraIntervals()
            ? this.ngModel[intervalGroupKey].length > 1
            : Object.keys(this.ngModel).length > 1;
    }

    protected removeTimeInterval(scheduleTimeIntervalKey: string, intervalIndex: number): void {
        if (this.hasExtraIntervals()) {
            this.ngModel[scheduleTimeIntervalKey].splice(intervalIndex, 1);
            this.updatePreviousIntervalGroup(scheduleTimeIntervalKey);
        } else {
            delete this.ngModel[scheduleTimeIntervalKey];
            // reset the keys in intervalGroup to keep the correct order from 0 to period length-1
            let newIntervalGroup: IChecklistTimeIntervalGroup = {};
            Object.keys(this.ngModel).forEach((key, index) => {
                newIntervalGroup[index] = this.ngModel[key];
            });
            this.ngModel = Object.assign({}, newIntervalGroup);
        }
        this.emitTimeIntervalChanged();
    }

    protected addTimeInterval(scheduleTimeIntervalKey: string): void {
        const newInterval = this.getNewInterval(scheduleTimeIntervalKey);
        if (this.hasExtraIntervals()) {
            this.ngModel[scheduleTimeIntervalKey].push(newInterval);
            this.updatePreviousIntervalGroup(scheduleTimeIntervalKey);
        } else {
            newInterval.IsEnabled = true;
            const newKey: string = `${Object.keys(this.ngModel).length}`;
            this.ngModel[newKey] = [newInterval];
            this.updatePreviousIntervalGroup(newKey);
        }
        this.emitTimeIntervalChanged();
    }

    private getNewInterval(scheduleTimeIntervalKey: string): IChecklistTimeInterval {
        return this.fiChecklist2ScheduleFactoryService.getNewInterval(
            scheduleTimeIntervalKey,
            this.intervalType(),
            this.isEnabledInterval(scheduleTimeIntervalKey),
        );
    }

    protected getPeriodName(periodIndex: number): string {
        return this.fiChecklist2ScheduleFormatService.getPeriodName(this.intervalType(), periodIndex);
    }

    protected onTimeIntervalChanged(timeIntervalIdentifier: IChecklistTimeIntervalIdentifier): void {
        this.updatePreviousIntervalGroup(timeIntervalIdentifier.scheduleTimeIntervalKey);
        this.emitTimeIntervalChanged(timeIntervalIdentifier);
    }

    protected emitTimeIntervalChanged(timeIntervalIdentifier?: IChecklistTimeIntervalIdentifier): void {
        this.doPropagateChanges();
        this.timeIntervalChanged.emit(timeIntervalIdentifier);
    }

    protected canCopyToNext(intervalGroupIndex: number, intervalIndex: number): boolean {
        const intervalGroupKeys = Object.keys(this.ngModel);
        return (
            intervalGroupIndex < intervalGroupKeys.length - 1 ||
            intervalIndex < this.ngModel[intervalGroupKeys[intervalGroupKeys.length - 1]].length - 1
        );
    }

    protected canCopyToAll(): boolean {
        const intervalGroupKeys = Object.keys(this.ngModel);
        return (
            intervalGroupKeys.length > 1 ||
            (intervalGroupKeys.length === 1 && this.ngModel[intervalGroupKeys[0]].length > 1)
        );
    }

    private copyTargetIntervalIndex: number = undefined;
    private isCopyToAllIntervals: boolean = undefined;
    protected onCopyToNext(currentScheduleTimeIntervalKey: string, interval: IChecklistTimeInterval): void {
        const allIntervals: IChecklistTimeInterval[] = this.fiChecklist2ScheduleIntervalService.getIntervalsFromGroup(
            this.ngModel,
        );
        const idx: number = allIntervals.indexOf(interval);
        if (idx !== -1 && idx < allIntervals.length - 1) {
            const intervalGroup = this.ngModel[currentScheduleTimeIntervalKey];
            const isLastIntervalInGroup = intervalGroup.indexOf(interval) === intervalGroup.length - 1;

            this.copyInterval(allIntervals[idx], allIntervals[idx + 1]);
            this.markTimeIntervalSettingsForCheck();
            this.copyTargetIntervalIndex = idx + 1;

            this.updatePreviousIntervalGroup(
                isLastIntervalInGroup ? currentScheduleTimeIntervalKey + 1 : currentScheduleTimeIntervalKey,
            );
            setTimeout(
                () => (this.copyTargetIntervalIndex = undefined),
                this.fiTimePickerHelperService.inputHighlightTimeout,
            );
        }
        this.emitTimeIntervalChanged();

        this.fiSnackBarService.showSnackbar({
            textMessage: this.appTextService.get('checklist_schedule.time_interval_copy_to_next_success'),
        });
    }

    protected onCopyToAll(sourceInterval: IChecklistTimeInterval): void {
        const allIntervals: IChecklistTimeInterval[] = this.fiChecklist2ScheduleIntervalService.getIntervalsFromGroup(
            this.ngModel,
        );

        allIntervals.forEach((interval: IChecklistTimeInterval) => {
            if (sourceInterval !== interval) {
                this.copyInterval(sourceInterval, interval);
                this.markTimeIntervalSettingsForCheck();
            }
        });
        this.isCopyToAllIntervals = true;
        setTimeout(() => (this.isCopyToAllIntervals = false), this.fiTimePickerHelperService.inputHighlightTimeout);
        this.setPreviousIntervalGroups();
        this.emitTimeIntervalChanged();
        this.fiSnackBarService.showSnackbar({
            textMessage: this.appTextService.get('checklist_schedule.time_interval_copy_to_all_success'),
        });
    }

    protected isHighlightedTimeInput(interval: IChecklistTimeInterval): boolean {
        const intervalIndex: number = this.fiChecklist2ScheduleIntervalService
            .getIntervalsFromGroup(this.ngModel)
            .indexOf(interval);

        return (
            intervalIndex === this.copyTargetIntervalIndex ||
            intervalIndex === this.copyTargetIntervalIndex - 1 ||
            this.isCopyToAllIntervals
        );
    }

    protected isInvalidTimeIntervalGroup(scheduleTimeIntervalKey: string): boolean {
        return this.invalidTimeIntervalIdentifier()?.scheduleTimeIntervalKey === scheduleTimeIntervalKey;
    }

    protected scrollToTimeIntervalGroup(timeIntervalIdentifier: IChecklistTimeIntervalIdentifier): void {
        const intervalGroupIndex = Object.keys(this.ngModel).indexOf(timeIntervalIdentifier.scheduleTimeIntervalKey);
        const intervalGroupElement = this.scheduleTimeIntervalWrappers.toArray()[intervalGroupIndex]?.nativeElement;
        intervalGroupElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    private copyInterval(sourceInterval: IChecklistTimeInterval, targetInterval: IChecklistTimeInterval): void {
        targetInterval.Start = this.copyIntervalData(sourceInterval.Start, targetInterval.Start);
        targetInterval.End = this.copyIntervalData(sourceInterval.End, targetInterval.End);
    }

    private copyIntervalData(
        sourceIntervalData: IChecklistTimeIntervalData,
        targetIntervalData: IChecklistTimeIntervalData,
    ): IChecklistTimeIntervalData {
        const result: IChecklistTimeIntervalData = cloneDeep(targetIntervalData);
        result.Minute = sourceIntervalData.Minute;
        result.Hour = sourceIntervalData.Hour;
        if (sourceIntervalData.hasOwnProperty('IsNextDay')) {
            result.IsNextDay = sourceIntervalData.IsNextDay;
        }
        if (this.isNotDailyOrWeeklyInterval()) {
            result.DayOfWeek = sourceIntervalData.DayOfWeek;
            result.Day = sourceIntervalData.Day;
            result.DataType = sourceIntervalData.DataType;
        }

        Object.keys(result).forEach((key: string) => !isDefined(result[key]) && delete result[key]);

        return result;
    }

    private readonly MondayIntervalKey = '1';
    private readonly SundayIntervalKey = '7';
    protected previousIntervalGroups: { [key: number]: IChecklistTimeInterval[] };
    private setPreviousIntervalGroups(): void {
        if (!this.isDailyInterval()) {
            this.previousIntervalGroups = undefined;
            return;
        }

        this.previousIntervalGroups = Object.keys(this.ngModel).reduce((acc, key) => {
            const modelKey = key === this.MondayIntervalKey ? this.SundayIntervalKey : (+key - 1).toString();
            acc[key] = this.ngModel[modelKey];
            return acc;
        }, {});
    }
    private updatePreviousIntervalGroup(changedGroupKey: string): void {
        if (!this.isDailyInterval()) {
            return;
        }

        const affectedGroupKey =
            changedGroupKey === this.SundayIntervalKey ? this.MondayIntervalKey : (+changedGroupKey - 1).toString();

        this.previousIntervalGroups = {
            ...this.previousIntervalGroups,
            [affectedGroupKey]: [...(this.ngModel[changedGroupKey] ?? [])],
        };
    }

    private markTimeIntervalSettingsForCheck(): void {
        this.timeIntervalSettingsComponents.toArray().forEach((instance) => instance.markForCheck());
    }

    protected readonly FiToggleSwitchSliderColorClass = FiToggleSwitchSliderColorClass;
}
